import { useQuery } from '@tanstack/react-query';
import { Loader2, AlertCircle } from 'lucide-react';
import { fetchCategories } from '../../utils/api';

const ACCENT_STYLES = [
  { color: 'text-accent1',   bg: 'bg-accent1/10 hover:bg-accent1/20',     border: 'border-accent1/30',   emoji: '🧸' },
  { color: 'text-accent2',   bg: 'bg-accent2/10 hover:bg-accent2/20',     border: 'border-accent2/30',   emoji: '🚗' },
  { color: 'text-secondary', bg: 'bg-secondary/10 hover:bg-secondary/20', border: 'border-secondary/30', emoji: '🚲' },
  { color: 'text-accent3',   bg: 'bg-accent3/10 hover:bg-accent3/20',     border: 'border-accent3/30',   emoji: '👗' },
  { color: 'text-primary',   bg: 'bg-primary/10 hover:bg-primary/20',     border: 'border-primary/30',   emoji: '🧩' },
  { color: 'text-accent2',   bg: 'bg-accent2/10 hover:bg-accent2/20',     border: 'border-accent2/30',   emoji: '📚' },
  { color: 'text-accent1',   bg: 'bg-accent1/10 hover:bg-accent1/20',     border: 'border-accent1/30',   emoji: '🎨' },
  { color: 'text-accent3',   bg: 'bg-accent3/10 hover:bg-accent3/20',     border: 'border-accent3/30',   emoji: '⚽' },
];

export default function CategoryList({
  title       = 'Browse Categories',
  showViewAll = true,
  limit       = 12,
}) {
  const { data: categories = [], isLoading, isError } = useQuery({
    queryKey: ['category-list', limit],
    queryFn:  () => fetchCategories({ per_page: limit, hide_empty: true }),
    staleTime: 5 * 60_000,
  });

  const siteUrl = window.themeData?.siteUrl ?? '';

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="flex items-center justify-between mb-10">
          <div>
            <h2 className="text-3xl md:text-4xl font-black text-textDark">{title}</h2>
            <p className="text-textLight mt-1">Tap a category to explore</p>
          </div>
          {showViewAll && (
            <a
              href={`${siteUrl}/shop/`}
              className="hidden md:block text-primary font-bold hover:text-primaryHover transition-colors"
            >
              View All →
            </a>
          )}
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-12 gap-3 text-primary">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="font-semibold text-textLight">Loading categories…</span>
          </div>
        )}

        {isError && (
          <div className="flex items-center justify-center py-12 gap-3 text-red-400">
            <AlertCircle className="w-5 h-5" />
            <span className="font-semibold">Could not load categories.</span>
          </div>
        )}

        {!isLoading && !isError && (
          <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide -mx-4 px-4">
            {categories.map((cat, i) => {
              const style = ACCENT_STYLES[i % ACCENT_STYLES.length];
              const href  = `${siteUrl}/product-category/${cat.slug}/`;
              const image = cat.image?.src;

              return (
                <a
                  key={cat.id}
                  href={href}
                  className={`flex-none snap-start flex flex-col items-center gap-3 w-32 p-5 rounded-2xl border-2 ${style.bg} ${style.border} transition-all hover:shadow-md hover:-translate-y-1 transform`}
                >
                  {image
                    ? <img src={image} alt={cat.name} className="w-12 h-12 object-cover rounded-xl" />
                    : <span className="text-5xl">{style.emoji}</span>
                  }
                  <div className="text-center">
                    <p className={`font-black text-sm ${style.color}`}>{cat.name}</p>
                    <p className="text-xs text-textLight mt-0.5">{cat.count} item{cat.count !== 1 ? 's' : ''}</p>
                  </div>
                </a>
              );
            })}

            {!isLoading && categories.length === 0 && (
              <p className="text-textLight py-8 px-4">No categories found.</p>
            )}
          </div>
        )}

        {showViewAll && (
          <div className="mt-6 text-center md:hidden">
            <a href={`${siteUrl}/shop/`} className="text-primary font-bold hover:text-primaryHover transition-colors">
              View All Categories →
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
